import type { EntityAdminRecord } from '@src/components/EntityAdmin/types';
import { get, noop } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import type { Allocation } from '../../../../../../types/Allocation';
import { EMPTY_ARRAY } from '../../../../../../utils';
import { AllocationsSelector } from '../../../../../AllocationsSelector';
import type { AllocationSelectDrawerOption } from '../../types';

export function AllocationsSelectorContent<TDrawerRecord extends EntityAdminRecord>({
  drawerOption,
  form,
  isEditing,
  onFormUpdate,
  touched,
  error,
}: {
  drawerOption: AllocationSelectDrawerOption<TDrawerRecord>;
  form: TDrawerRecord;
  isEditing: boolean;
  touched: boolean;
  error: string | undefined;
  onFormUpdate: (
    field: keyof TDrawerRecord,
    value: Allocation[],
    /**
     * Whether to suppress touching the field.
     * This is used to prevent the field from being touched when the initial render occurs.
     */
    suppressTouch?: boolean
  ) => void;
}) {
  const { getDropdownOptions, subAccountsByName, field, getIsDisabled, disabledWhenEditing, getIsRequired } =
    drawerOption;

  const allocations: Allocation[] | undefined = useMemo(() => get(form, field), [form, field]);

  const subAccountOptions = useMemo(() => {
    return getDropdownOptions(form, isEditing);
  }, [getDropdownOptions, form, isEditing]);

  const handleAllocationsChange = useCallback(
    (newAllocations: Allocation[]) => {
      onFormUpdate(field, newAllocations, false);
    },
    [onFormUpdate, field]
  );

  const isDisabled = useMemo(
    () => (disabledWhenEditing && isEditing) || getIsDisabled?.(form),
    [disabledWhenEditing, isEditing, getIsDisabled, form]
  );

  const required = getIsRequired?.(form) ?? false;

  return (
    <AllocationsSelector
      subAccountAllocations={allocations ?? EMPTY_ARRAY}
      allocationValueType="Percentage" // hard-coded for now
      subAccountOptions={subAccountOptions}
      subAccountsByName={subAccountsByName}
      onAllocationsChange={handleAllocationsChange}
      onAllocationsValueTypeChange={noop} // not allowed...
      hideTypeToggleButtons // hard-coded to percentage
      useAllocations // always true, otherwise we should just be a SearchSelect or similar
      disabled={isDisabled}
      required={required}
      /* AllocationSelector's touched and error interface is kinda weird. Gotta wrap it back into this object before passing in. */
      touched={useMemo(() => ({ subAccountAllocations: touched }), [touched])}
      errors={useMemo(() => ({ subAccountAllocations: error }), [error])}
    />
  );
}
